import { useState, useRef, useCallback, useEffect } from 'react';

interface NominatimResult {
  place_id: number;
  display_name: string;
  lat: string;
  lon: string;
  type: string;
  class: string;
  address?: {
    state?: string;
    city?: string;
    town?: string;
    village?: string;
    municipality?: string;
  };
}

interface AddressSearchBoxProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (lat: number, lng: number, zoom: number) => void;
}

function resolveZoom(result: NominatimResult): number {
  const { type, class: cls } = result;
  if (cls === 'boundary' || type === 'administrative') return 10;
  if (type === 'city') return 12;
  if (type === 'town' || type === 'village' || type === 'municipality') return 13;
  return 15;
}

function buildSubLabel(result: NominatimResult): string {
  const { address } = result;
  if (!address) return '';
  const parts = [
    address.state,
    address.city ?? address.town ?? address.village ?? address.municipality,
  ].filter(Boolean);
  return parts.join(' ');
}

export function AddressSearchBox({ open, onOpenChange, onSelect }: AddressSearchBoxProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<NominatimResult[]>([]);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const abortRef = useRef<AbortController | null>(null);

  // 展開時に入力欄へフォーカス
  useEffect(() => {
    if (open) {
      setTimeout(() => inputRef.current?.focus(), 50);
    } else {
      setQuery('');
      setResults([]);
      abortRef.current?.abort();
    }
  }, [open]);

  const search = useCallback(async (q: string) => {
    if (q.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }
    abortRef.current?.abort();
    abortRef.current = new AbortController();
    setLoading(true);
    try {
      const url =
        `https://nominatim.openstreetmap.org/search` +
        `?q=${encodeURIComponent(q)}` +
        `&format=json&countrycodes=jp&limit=5&addressdetails=1&accept-language=ja`;
      const res = await fetch(url, { signal: abortRef.current.signal });
      const data = (await res.json()) as NominatimResult[];
      setResults(data);
    } catch (err) {
      if ((err as Error).name !== 'AbortError') setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setQuery(val);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => void search(val), 300);
  };

  const handleSelect = (result: NominatimResult) => {
    onSelect(parseFloat(result.lat), parseFloat(result.lon), resolveZoom(result));
    onOpenChange(false);
  };

  const handleBlur = () => {
    setTimeout(() => {
      onOpenChange(false);
    }, 150);
  };

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      abortRef.current?.abort();
    };
  }, []);

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => onOpenChange(true)}
        className="bg-white rounded-full shadow-md p-2.5 flex items-center justify-center text-gray-600 hover:bg-gray-50 active:bg-gray-100"
        aria-label="住所・地名を検索"
      >
        <SearchIcon />
      </button>
    );
  }

  return (
    <div className="relative w-full">
      <div className="flex items-center bg-white rounded-full shadow-md px-3 py-2 gap-2">
        <SearchIcon className="text-gray-400 shrink-0" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="地名・住所を入力"
          className="flex-1 text-sm outline-none bg-transparent placeholder-gray-400"
        />
        {query.length > 0 && (
          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              setQuery('');
              setResults([]);
            }}
            className="text-gray-400 hover:text-gray-600 shrink-0"
            aria-label="クリア"
          >
            <ClearIcon />
          </button>
        )}
      </div>

      {(loading || results.length > 0 || (query.length >= 2 && !loading)) && (
        <ul className="absolute top-full left-0 right-0 mt-1 bg-white rounded-xl shadow-lg overflow-hidden z-10">
          {loading && <li className="px-4 py-3 text-sm text-gray-500">検索中...</li>}
          {!loading && results.length === 0 && query.length >= 2 && (
            <li className="px-4 py-3 text-sm text-gray-500">見つかりませんでした</li>
          )}
          {!loading &&
            results.map((r) => {
              const sub = buildSubLabel(r);
              const mainLabel = r.display_name.split(',')[0];
              return (
                <li key={r.place_id}>
                  <button
                    type="button"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => handleSelect(r)}
                    className="w-full text-left px-4 py-2.5 hover:bg-gray-50 active:bg-gray-100 border-b border-gray-100 last:border-0"
                  >
                    <p className="text-sm font-medium text-gray-800 truncate">{mainLabel}</p>
                    {sub && <p className="text-xs text-gray-500 truncate">{sub}</p>}
                  </button>
                </li>
              );
            })}
        </ul>
      )}
    </div>
  );
}

function SearchIcon({ className }: { className?: string }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
    >
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function ClearIcon() {
  return (
    <svg
      width="14"
      height="14"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}
